import React from 'react';
import { makeStyles } from '@material-ui/core/styles';
import Paper from '@material-ui/core/Paper';
import Typography from '@material-ui/core/Typography';
import Box from '@material-ui/core/Box';
import Button from '@material-ui/core/Button';
import Tooltip from '@material-ui/core/Tooltip';
import { Link } from 'react-router-dom';
import { StatusBadge, PathwayBadge, DoctorTypeBadge } from '../shared';
import {
  SURGERY_STATUS,
  SURGERY_STATUS_COLORS,
  PIPELINE_STEPS,
} from '../shared/constants';

const useStyles = makeStyles((theme) => ({
  card: {
    padding: 16,
    borderRadius: 8,
    cursor: 'pointer',
    transition: 'box-shadow 0.2s, transform 0.15s',
    '&:hover': {
      boxShadow: '0 4px 16px rgba(0,0,0,0.12)',
      transform: 'translateY(-1px)',
    },
    display: 'flex',
    flexDirection: 'column',
    height: '100%',
  },
  topRow: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'flex-start',
    marginBottom: 8,
  },
  patientName: {
    fontWeight: 700,
    fontSize: '1rem',
    color: '#333',
    lineHeight: 1.3,
  },
  patientNumber: {
    fontSize: '0.8rem',
    color: '#777',
  },
  badges: {
    display: 'flex',
    gap: 6,
    flexShrink: 0,
    marginLeft: 8,
  },
  procedure: {
    fontSize: '0.95rem',
    fontWeight: 600,
    color: '#444',
    marginBottom: 2,
  },
  opClass: {
    fontSize: '0.8rem',
    color: '#888',
    marginBottom: 12,
  },
  // Pipeline styles
  pipelineWrap: {
    display: 'flex',
    alignItems: 'center',
    margin: '8px 0 12px',
    padding: '8px 0',
  },
  pipelineStep: {
    display: 'flex',
    alignItems: 'center',
    flex: 1,
  },
  dot: {
    width: 14,
    height: 14,
    borderRadius: '50%',
    border: '2px solid #e0e0e0',
    backgroundColor: '#fff',
    flexShrink: 0,
    transition: 'all 0.2s',
  },
  dotFilled: {
    border: 'none',
  },
  dotCurrent: {
    width: 18,
    height: 18,
    border: 'none',
    boxShadow: '0 0 0 3px rgba(76,175,80,0.3)',
  },
  line: {
    flex: 1,
    height: 2,
    backgroundColor: '#e0e0e0',
    margin: '0 2px',
  },
  lineFilled: {
    backgroundColor: '#4CAF50',
  },
  // Bottom row
  bottomRow: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    flexWrap: 'wrap',
    gap: 6,
    marginTop: 'auto',
    paddingTop: 8,
    borderTop: '1px solid #f0f0f0',
  },
  metaItem: {
    fontSize: '0.78rem',
    color: '#666',
  },
  metaLabel: {
    color: '#999',
    marginRight: 4,
  },
  actions: {
    display: 'flex',
    gap: 6,
    marginTop: 8,
  },
  cardLink: {
    textDecoration: 'none',
    color: 'inherit',
    display: 'flex',
    flexDirection: 'column',
    height: '100%',
  },
}));

/**
 * Determine the active step index in the pipeline based on the surgery status.
 * Returns -1 if cancelled or unknown.
 */
const getActiveStepIndex = (status) => {
  for (let i = PIPELINE_STEPS.length - 1; i >= 0; i--) {
    if (PIPELINE_STEPS[i].statusMatch.includes(status)) {
      return i;
    }
  }
  return -1;
};

const SurgeryCard = ({ surgery }) => {
  const classes = useStyles();

  const {
    id,
    patient_name,
    patient_number,
    procedure,
    status,
    pathway,
    operation_class,
    operation_date,
    estimated_start_time,
    theatre_name,
    lead_surgeon_name,
    lead_surgeon_type,
  } = surgery;

  const activeIdx = getActiveStepIndex(status);
  const isCancelled = status === SURGERY_STATUS.CANCELLED;
  const showStart =
    status === SURGERY_STATUS.PENDING ||
    status === SURGERY_STATUS.PRE_OP_COMPLETE;
  const showComplete = status === SURGERY_STATUS.STARTED;

  return (
    <Paper className={classes.card} elevation={1}>
      <Link to={`/surgery/detail/${id}`} className={classes.cardLink}>
        {/* Top Row */}
        <div className={classes.topRow}>
          <div>
            <Typography className={classes.patientName}>
              {patient_name}
            </Typography>
            <Typography className={classes.patientNumber}>
              {patient_number}
            </Typography>
          </div>
          <div className={classes.badges}>
            {pathway && <PathwayBadge pathway={pathway} />}
            <StatusBadge status={status} />
          </div>
        </div>

        {/* Middle */}
        <Typography className={classes.procedure}>{procedure}</Typography>
        {operation_class && (
          <Typography className={classes.opClass}>
            Class: {operation_class}
          </Typography>
        )}

        {/* Pipeline */}
        {!isCancelled && (
          <div className={classes.pipelineWrap}>
            {PIPELINE_STEPS.map((step, idx) => {
              const filled = idx <= activeIdx;
              const isCurrent = idx === activeIdx;
              const color = filled
                ? SURGERY_STATUS_COLORS[status] || '#4CAF50'
                : undefined;

              return (
                <React.Fragment key={step.key}>
                  <Tooltip title={step.label} arrow placement="top">
                    <div
                      className={`${classes.dot} ${
                        filled ? classes.dotFilled : ''
                      } ${isCurrent ? classes.dotCurrent : ''}`}
                      style={
                        filled ? { backgroundColor: color } : undefined
                      }
                    />
                  </Tooltip>
                  {idx < PIPELINE_STEPS.length - 1 && (
                    <div
                      className={`${classes.line} ${
                        idx < activeIdx ? classes.lineFilled : ''
                      }`}
                      style={
                        idx < activeIdx
                          ? { backgroundColor: color }
                          : undefined
                      }
                    />
                  )}
                </React.Fragment>
              );
            })}
          </div>
        )}

        {/* Bottom Row */}
        <div className={classes.bottomRow}>
          <Box display="flex" alignItems="center" style={{ gap: 12 }}>
            {operation_date && (
              <span className={classes.metaItem}>
                <span className={classes.metaLabel}>Date:</span>
                {operation_date}
                {estimated_start_time ? ` ${estimated_start_time}` : ''}
              </span>
            )}
            {theatre_name && (
              <span className={classes.metaItem}>
                <span className={classes.metaLabel}>Theatre:</span>
                {theatre_name}
              </span>
            )}
          </Box>
          {lead_surgeon_name && (
            <Box display="flex" alignItems="center" style={{ gap: 6 }}>
              <span className={classes.metaItem}>{lead_surgeon_name}</span>
              {lead_surgeon_type && (
                <DoctorTypeBadge doctorType={lead_surgeon_type} />
              )}
            </Box>
          )}
        </div>
      </Link>

      {/* Action buttons */}
      <div className={classes.actions}>
        <Button
          component={Link}
          to={`/surgery/detail/${id}`}
          size="small"
          variant="outlined"
          color="primary"
          onClick={(e) => e.stopPropagation()}
        >
          View
        </Button>
        {showStart && (
          <Button size="small" variant="contained" color="primary">
            Start
          </Button>
        )}
        {showComplete && (
          <Button
            size="small"
            variant="contained"
            style={{ backgroundColor: '#009688', color: '#fff' }}
          >
            Complete
          </Button>
        )}
      </div>
    </Paper>
  );
};

export default SurgeryCard;
